import { FC, useRef, useEffect, useState, useContext, useMemo } from 'react';
import { uniqBy } from 'lodash';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { ThemeContext } from 'styled-components';
import { useTranslation } from 'react-i18next';

import { ObjectiveItem } from '~src/store/okrs/interfaces';
import { translateContent } from '~src/services/translationServices';
import AvatarsRow from '~src/shared/avatarsRow/AvatarsRow';
import TooltipList from '~src/shared/tooltips/TooltipList';
import ProgressBar from '~src/shared/ProgressBar';
import { ownerToSegment } from '~src/modules/okrs/manageObjectives/shared/utils';
import SvgIcon from '~src/shared/SvgIcon';

import { NameCol, OwnersCol, DueDateCol, StatusCol, ProgressCol, LabelsCol } from './shared.sc';
import { ObjectiveTile, LabelsList, Label, LabelOverlay } from './objectiveRow.sc';

interface Props {
  itemData: ObjectiveItem;
}

const ObjectiveRow: FC<Props> = props => {
  const [t] = useTranslation();
  const themeContext = useContext(ThemeContext);
  const labelsRef = useRef<HTMLDivElement>(null);
  const [restLabelsCount, setRestLabelsCount] = useState<number>();

  useEffect(() => {
    getRestLabelsCount();
  }, []);

  const getRestLabelsCount = () => {
    if (!labelsRef.current) return null;
    const labelEls = labelsRef.current.querySelectorAll('div');
    let fitCount = 1;
    for (let i = 1; i < labelEls.length; i++) {
      const labelEl = labelEls[i];
      const containerWidth = labelsRef.current.clientWidth - 67;
      if (labelEl.offsetLeft >= containerWidth) {
        break;
      }
      fitCount = i + 1;
    }
    if (labelEls.length > fitCount) {
      setRestLabelsCount(labelEls.length - fitCount);
    }
  };

  const getRestCountStr = (count: number) => (count > 999 ? `${Math.round(count / 1000)}k` : count);
  const progressColor = { 29: themeContext.gold.a10, 69: themeContext.green.a10 };

  const mergedOwners = useMemo(
    () =>
      uniqBy([...(props.itemData.owners || []), ...(props.itemData.key_result_owners || [])], el => el.uuid),
    [props.itemData],
  );

  return (
    <Link to={`/okrs/${props.itemData.id}`}>
      <ObjectiveTile>
        <NameCol>
          {translateContent(props.itemData.name)}{' '}
          {props.itemData.has_sub_objectives && <SvgIcon name="iconPlusBox" />}
        </NameCol>
        <ProgressCol>
          <span>{props.itemData.progress}%</span>
          <ProgressBar
            progress={props.itemData.progress}
            width={90}
            height={5}
            staticProgress
            progressColor={progressColor}
          />
        </ProgressCol>
        <StatusCol>{t(`x.okr.objectives.status.${props.itemData.real_status}`)}</StatusCol>
        <DueDateCol>{moment(props.itemData.due_date).format('D MMM, YYYY')}</DueDateCol>
        <OwnersCol>
          <AvatarsRow userData={mergedOwners.map(ownerToSegment)} />
        </OwnersCol>
        <LabelsCol>
          <LabelsList ref={labelsRef}>
            {props.itemData.labels ? (
              <>
                {props.itemData.labels.slice(0, 20).map(label => (
                  <Label key={label.id}>{label.name}</Label>
                ))}
                <LabelOverlay $withCounter={!!restLabelsCount}>
                  {restLabelsCount && (
                    <TooltipList
                      items={props.itemData.labels}
                      restCount={restLabelsCount}
                      placement="bottom"
                      overlayStyle={{ fontSize: '0.8rem' }}
                    >
                      <Label>+{getRestCountStr(restLabelsCount)}</Label>
                    </TooltipList>
                  )}
                </LabelOverlay>
              </>
            ) : (
              '-'
            )}
          </LabelsList>
        </LabelsCol>
      </ObjectiveTile>
    </Link>
  );
};
export default ObjectiveRow;
